import { TableRef } from '@/components/Table';
import { WithLoader } from '@/components/WithLoader';
import { useConfigContext } from '@/context/configContext';
import {
    MediaSpendSeasonalityTable,
    MediaSpendSeasonalityTableProps,
} from '@/dashboards/CampaignOptimization/components/MediaSpendSeasonality/MediaSpendSeasonalityTable';
import { useOptimizationSession } from '@/dashboards/CampaignOptimization/hooks/useOptimizationSession';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { isPeriodKey } from '@/dashboards/utils/data/structureMediaBudget';
import { getPeriodOptionsForModel } from '@/utils/model';
import { capitalizeFirstLetter } from '@/utils/stringUtils';
import { TabButtons, Typography, Button } from '@analytical-alley/ui';
import { IconDownload } from '@tabler/icons-react';
import moment from 'moment';
import React, { useCallback, useMemo, useRef, useState } from 'react';

export const MediaSpendSeasonality = () => {
    const {
        data: { getMediaBudgetTableData },
        disabledChannels,
        isDataLoading,
        isDataFetching,
    } = useOptimizationSession();

    const {
        project: { interval },
        variableNameMap,
    } = useDashboardGlobalContext();

    const { currency } = useConfigContext();

    const availablePeriodOptions = getPeriodOptionsForModel(interval, [
        'daily',
        'weekly',
        'monthly',
    ]);

    const [activeTabIndex, setActiveTabIndex] = useState(0);

    const selectedOption = availablePeriodOptions[activeTabIndex];

    const formatPeriodHeader: MediaSpendSeasonalityTableProps['formatPeriodHeader'] =
        useCallback(
            (period) => {
                switch (selectedOption) {
                    case 'daily':
                        return moment(period).format('MMM D, YY');
                    case 'monthly':
                        return moment(period).format('MMM, YY');
                    case 'weekly': {
                        const m = moment(period, 'YYYY-MM-DD');
                        return `Week ${m.isoWeek()}, ${m.isoWeekYear()}`;
                    }
                    default:
                        return '';
                }
            },
            [selectedOption],
        );

    const rawTableData = useMemo(() => {
        if (selectedOption) {
            return getMediaBudgetTableData(selectedOption);
        }
        return [];
    }, [getMediaBudgetTableData, selectedOption]);

    const disabledChannelsNames = useMemo(() => {
        return disabledChannels.map((channel) => variableNameMap[channel]);
    }, [disabledChannels, variableNameMap]);

    const tableData = rawTableData.filter(
        (row) => !disabledChannelsNames.includes(row.variable),
    );

    const tableRef = useRef<TableRef>(null);

    const getTotalsRow = () => {
        if (!tableData?.length) return undefined;

        // Create a totals object with category and variable set to 'Total'
        const totalsRow = {
            category: 'Total',
            variable: '',
            total: 0,
        };

        // Get period keys (columns that represent time periods)
        const periodKeys = Object.keys(tableData[0] || {}).filter(isPeriodKey);

        // Initialize total for each period to zero
        periodKeys.forEach((period) => {
            totalsRow[period] = 0;
        });

        // Sum up values for each period and the overall total
        tableData.forEach((row) => {
            // Add to the total column sum
            totalsRow.total += row.total;

            // Add to each period column sum
            periodKeys.forEach((period) => {
                totalsRow[period] += row[period];
            });
        });

        return totalsRow;
    };

    const downloadXls = () => {
        tableRef.current?.downloadXls(
            `media-spend-seasonality-${selectedOption}`,
            {
                addDate: true,
                currency: currency,
            },
            getTotalsRow(),
        );
    };

    return (
        <div className="relative">
            <WithLoader isLoading={isDataLoading} isFetching={isDataFetching}>
                <div className="flex items-center mb-4">
                    <Typography
                        variant="titleS"
                        tooltip="Your execution roadmap. Precise daily/weekly/monthly budgets for each media channel to reach your selected target."
                    >
                        Media Budget Recommendations
                    </Typography>
                    <TabButtons
                        aria-label="Periodicity"
                        variant="pills"
                        className="gap-2 ml-2"
                        onActiveTabChange={(activeTab) => {
                            setActiveTabIndex(activeTab);
                        }}
                        activeTab={activeTabIndex}
                    >
                        {availablePeriodOptions.map((periodName) => {
                            return (
                                <TabButtons.Item
                                    key={periodName}
                                    title={capitalizeFirstLetter(periodName)}
                                />
                            );
                        })}
                    </TabButtons>
                    <Button
                        className="glass-tile ml-2 w-8 h-8 flex justify-center items-center p-2 rounded-md shadow-lg hover:bg-white/30"
                        onClick={downloadXls}
                        tooltip="Download the detailed optimisation report."
                    >
                        <IconDownload />
                    </Button>
                </div>
                <MediaSpendSeasonalityTable
                    formatPeriodHeader={formatPeriodHeader}
                    data={tableData}
                    tableRef={tableRef}
                />
            </WithLoader>
        </div>
    );
};
